import { AnchorLayerType, LayerIdEnum } from '@/utils/constant';
import { createBaiduWmsLayer } from './wmsLayer';
import { readPointCoordinates, toBdPoint } from './geometry';
import {
  returnNormalAnchorFillColor,
  returnSelectedAnchorFillColor,
} from './styles';
import { LayerRegistry } from './layerRegistry';
import { OverlayManager } from './overlays';
import { createArrowLine } from './lineLayer';
import type { ArrowLineHandle, ArrowLineOptions } from './lineLayer';
import { DrawingInteraction } from './interactions/DrawingInteraction';
import { PopupManager } from './overlays/PopupFactory';
import { MapEventBus, MapEventManager } from './events';
import { BaiduMapAdapter } from './adapters/BaiduMapAdapter';
import { searchLocalPois, type LocalSearchPoi } from './search';
import {
  createVectorTileLayer,
  removeVectorTileLayer,
  setVectorTileLayerVisible,
} from './vectorTileLayer';
import type { FeatureLike, MapEngineProps, LngLat, IMapEngine, EventHandler } from './types';

export { returnNormalAnchorFillColor, returnSelectedAnchorFillColor };

/**
 * 地图编辑器工具类 — 门面（Facade）
 *
 * 层级：map-core（"地图怎么动"）
 *
 * 职责：
 * 1. 初始化地图引擎（通过 IMapEngine.initSync）和 4 个子模块
 * 2. 管理地图生命周期（init + destroy + 事件监听注册/注销）
 * 3. 对外暴露统一 API，将调用委托给子模块（PopupManager / OverlayManager / LayerRegistry / DrawingSession）
 *
 * 不关心：业务逻辑、编辑器模式、React 状态
 *
 * 子模块分工：
 * - PopupManager:        弹窗创建/定位/清理
 * - OverlayManager:      覆盖物创建/样式/高亮/选中状态
 * - LayerRegistry:       图层创建/注册/可见性/批量操作
 * - DrawingInteraction:  绘制交互状态机（点击添加顶点、双击完成、ESC 取消、RAF 预览）
 */

class MapEngine {
  options: MapEngineProps & Record<string, any>;
  container: HTMLElement;
  centerPoint: LngLat;
  /** 引擎抽象层 */
  engine: IMapEngine;
  /** 事件总线 */
  eventBus: MapEventBus;
  /** 地图事件管理器（统一原生事件订阅/清理） */
  events: MapEventManager;
  popupManager!: PopupManager;
  layers!: LayerRegistry;
  overlays!: OverlayManager;
  drawingInteraction!: DrawingInteraction;
  zoomEndHandler: EventHandler | null = null;

  /** 向后兼容：获取原生 BMapGL.Map 实例 */
  get map() {
    return this.engine.getNativeMap();
  }

  constructor(options: MapEngineProps) {
    this.options = options as MapEngineProps & Record<string, any>;
    this.container = options.container;
    this.centerPoint = options.centerPoint;
    this.eventBus = new MapEventBus();
    this.engine = new BaiduMapAdapter();
    this.events = new MapEventManager(this.engine, this.eventBus);
    this.initMap();
    this.initAnchorLayer();
    this.initAttributeMarkLayer();
  }

  initMap = () => {
    this.engine.initSync(this.container, {
      center: this.centerPoint,
      zoom: 18,
      minZoom: 3,
      maxZoom: 25,
      enableScrollWheelZoom: true,
      displayOptions: { building: false },
    });

    const nativeMap = this.engine.getNativeMap();
    this.popupManager = new PopupManager(nativeMap);
    this.layers = new LayerRegistry(nativeMap);
    this.overlays = new OverlayManager(nativeMap, this.layers, {
      options: this.options,
      isDrawing: () => this.drawingInteraction?.isDrawing() ?? false,
      changeMapCenter: this.changeMapCenter,
    });
    this.drawingInteraction = new DrawingInteraction(
      nativeMap,
      this.engine,
      this.events,
      this.layers,
      {
        options: this.options,
        drawPolygon: this.drawPolygon,
      }
    );
    this.events.bind();

    this.zoomEndHandler = () => {
      this.layers.handleZoomChange(nativeMap.getZoom());
    };
    this.events.on('map:zoomend', this.zoomEndHandler);
  };

  initAnchorLayer() {
    this.createLayer(LayerIdEnum.ANCHOR_LAYER);
  }

  initAttributeMarkLayer = () => {
    this.createLayer(LayerIdEnum.ATTRIBUTE_MARK_LAYER);
  };

  getCenter = (): [number, number] => {
    return readPointCoordinates(this.map.getCenter());
  };

  getZoom = () => this.map.getZoom();

  changeMapCenter = (coordinates: any) => {
    this.centerPoint = coordinates;
    this.map.panTo(toBdPoint(coordinates));
  };

  changeMapSize = () => {
    this.map?.checkResize?.();
  };

  addOverlay(element: any) {
    this.popupManager.addOverlay(element);
  }

  clearOverlay() {
    this.popupManager.clearAll();
  }

  destroy() {
    this.clearOverlay();
    this.cancelDrawingStatus();
    this.layers.clearAll();
    this.events.clear();
    // 清掉 popupManager.addOverlay 非 popup 分支直接加到地图上的覆盖物
    this.engine.getNativeMap()?.clearOverlays?.();
    this.engine.destroy();
  }

  addLayers = (layers: { layer: any; layerId: string }[]) =>
    this.layers.addLayers(layers);

  createLine(
    coordinates: any,
    style: any,
    id?: string | number,
    layerId?: string
  ) {
    return this.overlays.createLine(coordinates, style, id, layerId);
  }

  /** 箭头线图层（LineLayer + 纹理箭头，不经 LayerRegistry，返回 handle 自持生命周期） */
  createArrowLine = (
    coordinates: [number, number][],
    options: ArrowLineOptions
  ): ArrowLineHandle => createArrowLine(this.map, coordinates, options);

  drawPolygon = (
    id: string | number,
    points: any,
    type: AnchorLayerType,
    val: any
  ) => this.overlays.drawPolygon(id, points, type, val);

  searchPois = (
    keyword: string,
    onComplete: (list: LocalSearchPoi[]) => void
  ) => {
    searchLocalPois(this.map, keyword, onComplete);
  };

  startDraw = () => this.drawingInteraction.start();

  setDrawingStyle = (override: Partial<{
    lineColor: string;
    polygonFill: string;
    polygonStroke: string;
    pointFill: string;
  }>) => {
    this.drawingInteraction.setDrawingStyle(override);
  };

  resetDrawingStyle = () => {
    this.drawingInteraction.setDrawingStyle({});
  };

  deletePolygon(id: number | string) {
    this.overlays.deletePolygon(id);
  }

  clearDraw() {
    this.layers.clearDrawnFeatures();
    this.overlays.clearSelection();
  }

  cancelDrawingStatus() {
    this.drawingInteraction.cancel();
  }

  highLightPolygon = (
    id: number,
    options?: { panTo?: boolean }
  ): FeatureLike | undefined => this.overlays.highLightPolygon(id, options);

  hasPolygon = (id: number | string) =>
    this.layers.polygons.some((feature) => feature.getId() === id);

  createLayer = (layerId: string) =>
    this.layers.createLayer(layerId);

  getLayer = (layerId: string | undefined | null) =>
    this.layers.getLayer(layerId);

  changeLayerVisible = (layerId: any, visible: boolean) =>
    this.layers.changeLayerVisible(layerId, visible);

  clearLayer(layerId: string) {
    this.layers.clearLayer(layerId);
  }

  createPointFeature = (
    coordinates: any,
    imgSrc: string,
    pointId?: string,
    otherStyle?: any,
    anchor?: any,
    layerId?: string
  ) =>
    this.overlays.createPointFeature(
      coordinates,
      imgSrc,
      pointId,
      otherStyle,
      anchor,
      layerId
    );

  changeSearchPoint = (coordinates: [number, number]) => {
    this.changeMapCenter(coordinates);
  };

  setDrawCallback = (callback: AnyFunc) => {
    this.options.drawCallBack = callback;
  };

  setSelectCallback = (callback: AnyFunc) => {
    this.options.selectCallBack = callback;
  };

  createPopup = (
    id: string | number,
    element: HTMLElement,
    coordinates: [number, number]
  ) => this.popupManager.createPopup(id, element, coordinates);

  exportGeometry = (feature: FeatureLike) =>
    this.overlays.exportGeometry(feature);

  createWmsLayer = (config: { url: string; params: any; layerId: string }) => {
    return createBaiduWmsLayer(this.map, {
      url: config.url,
      params: config.params,
      layerId: config.layerId,
      debug: process.env.NODE_ENV === 'development',
    });
  };

  addVectorTileLayer(options: {
    url: string;
    minZoom?: number;
    maxZoom?: number;
    opacity?: number;
    useThumbData?: boolean;
    styleMap?: Record<number, any>;
  }): any {
    return createVectorTileLayer(this.map, options);
  }

  removeVectorTileLayer(layer: any): void {
    removeVectorTileLayer(this.map, layer);
  }

  setVectorTileLayerVisible(layer: any, visible: boolean): any {
    return setVectorTileLayerVisible(this.map, layer, visible);
  }
}

export default MapEngine;
